import React, { useEffect, useState } from 'react';
import {
    Modal, Button, Row, Col,
    Table, Tag, message
} from 'antd';
import {
    DownloadOutlined,
    CopyOutlined,
    MailOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Logo from "../../../JPEG/Logo-pdf.png";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Config from '../../../API/Config';

dayjs.extend(utc);
dayjs.extend(timezone);

const InvoicePreviewModal = ({ visible, onCancel, lead }) => {
    const [loading, setLoading] = useState(false);
    const [currentPSTDate, setCurrentPSTDate] = useState('');

    useEffect(() => {
        const nowInPST = dayjs().tz('America/Los_Angeles').format('MM/DD/YYYY');
        setCurrentPSTDate(nowInPST);
    }, []);

    // Format any date to PST
    const formatToPST = (date) => {
        return dayjs(date).tz('America/Los_Angeles').format('MM/DD/YYYY');
    };

    // Format date to MM/DD/YYYY
    const formatDate = (dateString) => {
        if (!dateString) return new Date().toLocaleDateString('en-US');
        return new Date(dateString).toLocaleDateString('en-US');
    };

    const getInvoiceDate = (dateString) => {
        return dateString
            ? formatToPST(dateString)
            : currentPSTDate || formatToPST(new Date());
    };
    
    // Calculate due date (30 days from invoice date)
    const calculateDueDate = () => {
        const invoiceDate = new Date();
        const dueDate = new Date(invoiceDate);
        dueDate.setDate(dueDate.getDate() + 30);
        return dueDate.toLocaleDateString('en-US');
    };

    // Prepare invoice data from lead
    const prepareInvoiceData = () => {
        if (!lead) return null;

        const companyInfo = {
            name: "PREMIUM USED AUTO PARTS LLC",
            address: "10940 White rock Rd, #Suite 210",
            cityStateZip: "Rancho Cordova,CA 95670",
              phone: "1-800-606-1107",
  email: "orders@premiumusedautoparts.com",

        };

        const billingInfo = {
            name: lead.personalInfo?.fullName || "UNKNOWN",
            address: lead.billingAddress?.street || "23896 Santa Fe Rd",
            cityStateZip: `${lead.billingAddress?.city || 'Escalon'}, ${lead.billingAddress?.state || 'CA'} ${lead.billingAddress?.zip || '95320'}`,
        };

        const vehicleDetails = lead.vehicleInfo || {};
        const partDescription = `${vehicleDetails.year || '2005'} ${vehicleDetails.make || 'Nissan'} ${vehicleDetails.model || 'Armada'} ${vehicleDetails.part || 'Engine(5.6L, VIN A, 4th digit, VK56DE)'}`;

        const lineItems = [{
            key: 1,
            description: `${partDescription} WARRANTY ${lead.warranty || '3 MONTH'}`,
            vin: vehicleDetails.vin || lead.vehicleInfo?.vin || '5N1AA0BA85N713376',
            qty: 1,
            rate: lead.totalAmount || 1833.00,
            amount: lead.totalAmount || 1833.00,
        }];

        return {
            invoiceNumber: lead.orderNumber || `INV-${lead._id?.slice(-8).toUpperCase()}`,
            invoiceDate: formatDate(new Date()),
            dueDate: calculateDueDate(),
            companyInfo,
            billingInfo,
            lineItems,
            summary: {
                subtotal: lead.totalAmount || 1833.00,
                total: lead.totalAmount || 1833.00,
            }
        };
    };

    const invoiceData = prepareInvoiceData();

    if (!invoiceData) {
        return null;
    }

    // Invoice table columns
    const columns = [
        {
            title: '#',
            dataIndex: 'key',
            key: 'key',
            width: 50,
            render: (text) => <span style={{ fontWeight: 'bold' }}>{text}</span>
        },
        {
            title: 'Item Details',
            dataIndex: 'description',
            key: 'description',
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>{text}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        VIN: {record.vin}
                    </div>
                </div>
            ),
        },
        {
            title: 'Qty',
            dataIndex: 'qty',
            key: 'qty',
            width: 80,
            align: 'center',
        },
        {
            title: 'Rate',
            dataIndex: 'rate',
            key: 'rate',
            width: 100,
            align: 'right',
            render: (value) => `$${value}`,
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            width: 100,
            align: 'right',
            render: (value) => `$${value}`,
        },
    ];

   const handleSendToMail = async () => {
    try {
        setLoading(true);
        
        // Make sure lead exists and has an ID
        if (!lead || !lead._id) {
            message.error('Lead information is missing');
            return;
        }

        // ✅ FIXED: Use the correct endpoint that matches your working curl command
        // Your curl shows it works with: /api/emails/send-invoice
        const response = await fetch(`${Config.baseURL}/api/emails/send-invoice`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                leadId: lead._id
            })
        });

        const result = await response.json();

        if (result.success) {
            message.success(result.message || 'Invoice sent successfully!');
            onCancel(); // Optional: close modal after successful send
        } else {
            message.error(result.message || result.error || 'Failed to send invoice');
        }
    } catch (error) {
        console.error('Error sending invoice email:', error);
        message.error('Failed to send invoice. Please try again.');
    } finally {
        setLoading(false);
    }
};

    const handleDownloadPDF = async () => {
        try {
            setLoading(true);

            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
            });

            // ── 1. LOGO (top-left) ──────────────────────────────────────────────
            try {
                doc.addImage(Logo, 'PNG', 20, 8, 50, 22);
            } catch (e) {
                console.warn('Logo could not be loaded:', e);
            }

            // ── 2. COMPANY INFO (top-right) ─────────────────────────────────────
        doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.setTextColor('#004183');
doc.text(invoiceData.companyInfo.name, 190, 17, { align: 'right' });

      doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
      doc.setFontSize(9);
      doc.text(invoiceData.companyInfo.address, 190, 23, { align: 'right' });
      doc.text(invoiceData.companyInfo.cityStateZip, 190, 28, { align: 'right' });
      doc.text(invoiceData.companyInfo.phone, 190, 33, { align: 'right' });
      doc.text(invoiceData.companyInfo.email, 190, 38, { align: 'right' });

      // DIVIDER LINE
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.4);
      doc.line(20, 40, 190, 40);

            // ── 4. BILL TO (left) ───────────────────────────────────────────────
            let yPosition = 46;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(10);
              doc.setTextColor('#004183');
            doc.text('Bill To:', 20, yPosition);
            yPosition += 5;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor('#000');
            doc.text(invoiceData.billingInfo.name,        20, yPosition); yPosition += 5;
            doc.text(invoiceData.billingInfo.address,     20, yPosition); yPosition += 5;
            doc.text(invoiceData.billingInfo.cityStateZip,20, yPosition); yPosition += 5;

            // ── 5. INVOICE TITLE + DETAILS (right) ─────────────────────────────
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(26);
              doc.setTextColor('#004183');
            doc.text('INVOICE', 190, 54, { align: 'right' });

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
                doc.setTextColor('#000');
            doc.text(`Invoice No:  ${invoiceData.invoiceNumber}`, 190, 61, { align: 'right' });

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor('#000');
            doc.text('Invoice Date:', 152, 67);
            doc.setFont('helvetica', 'normal');
            doc.text(currentPSTDate, 190, 67, { align: 'right' });

            doc.setFont('helvetica', 'bold');
            doc.text('Due Date:', 152, 73);
            doc.setTextColor('#000');
            doc.setFont('helvetica', 'normal');
            doc.text(currentPSTDate, 190, 73, { align: 'right' });

            yPosition = 80;

            // ── 6. ITEMS TABLE ──────────────────────────────────────────────────
            const tableColumn = ['#', 'Item Details', 'Qty', 'Rate', 'Amount'];
// 1. Map the rows and separate the Warranty text
      const tableRows = invoiceData.lineItems.map(item => {
        let desc = item.description || "";
        let warrantyText = "";
        
        // Find where "WARRANTY" starts and split it
        const warrantyIndex = desc.toUpperCase().lastIndexOf("WARRANTY");
        if (warrantyIndex !== -1) {
          warrantyText = desc.substring(warrantyIndex);
          desc = desc.substring(0, warrantyIndex).trim();
        }

        // ADDED AN EXTRA \n BEFORE VIN to force the row to be a bit taller
        const heightMeasurementText = `${desc}\n${warrantyText}\n\nVIN: ${item.vin}`;

        return [
          item.key,
          heightMeasurementText, 
          item.qty,
          `$${item.rate.toFixed(2)}`,
          `$${item.amount.toFixed(2)}`
        ];
      });

     // 2. Draw the table with the cell hooks
      doc.autoTable({
        startY: yPosition,
        head: [tableColumn],
        body: tableRows,
        theme: 'grid',
        headStyles: {
          fillColor: '#004183',
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 9,
          halign: 'center',
          cellPadding: 3,
          minCellHeight: 10,
        },
        styles: {
          fontSize: 9,
          cellPadding: 3,
          textColor: [0, 0, 0],
          minCellHeight: 16,
        },
        columnStyles: {
          0: { cellWidth: 12, halign: 'center' },
          1: { cellWidth: 90 },
          2: { cellWidth: 18, halign: 'center' },
          3: { cellWidth: 28, halign: 'right' }, // Right-aligned for amounts
          4: { cellWidth: 28, halign: 'right' }, // Right-aligned for amounts
        },
        didParseCell(data) {
          // 1. Force the placeholder text to be WHITE (invisible) before it draws
          if (data.section === 'body' && data.column.index === 1) {
            data.cell.styles.textColor = [255, 255, 255]; 
          }
        },
        willDrawCell(data) {
          // 2. Safely attempt to empty the text array just in case
          if (data.section === 'body' && data.column.index === 1) {
            data.cell.text = ['']; 
          }
        },
        didDrawCell(data) {
          if (data.section === 'body' && data.column.index === 1) {
            const { x, y } = data.cell;
            const item = invoiceData.lineItems[data.row.index];
            if (!item) return;

            // Separate Warranty text again for drawing
            let desc = item.description || "";
            let warrantyText = "";
            const warrantyIndex = desc.toUpperCase().lastIndexOf("WARRANTY");
            if (warrantyIndex !== -1) {
              warrantyText = desc.substring(warrantyIndex);
              desc = desc.substring(0, warrantyIndex).trim();
            }

            // Start slightly lower to match top padding
            let currentY = y + 7; 

            // Draw Part Description (Bold, Black)
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(0, 0, 0); 
            const descLines = doc.splitTextToSize(desc, data.cell.width - 4);
            doc.text(descLines, x + 2, currentY);
            
            // Advance Y (using 5 instead of 4.5 for more breathing room)
            currentY += (descLines.length * 5); 

            // Draw Warranty on a separate line (Bold, Black)
            if (warrantyText) {
              doc.setTextColor(0, 0, 0); 
              const warrantyLines = doc.splitTextToSize(warrantyText, data.cell.width - 4);
              doc.text(warrantyLines, x + 2, currentY);
              
              currentY += (warrantyLines.length * 5);
            }

            // Add an explicit extra gap before the VIN
            currentY += 1.5; 

            // Draw VIN (Normal text, Grey)
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(80, 80, 80);
            const vinLines = doc.splitTextToSize(`VIN: ${item.vin}`, data.cell.width - 4);
            doc.text(vinLines, x + 2, currentY);
          }
        },
      });

            yPosition = doc.lastAutoTable.finalY + 5;

            // ── 7. TOTALS ───────────────────────────────────────────────────────
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(0, 0, 0);
            doc.text('Sub Total', 150, yPosition);
            doc.text(`$${invoiceData.summary.subtotal}`, 190, yPosition, { align: 'right' });

            yPosition += 6;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            doc.text('Total', 150, yPosition);
            doc.text(`$${invoiceData.summary.total}`, 190, yPosition, { align: 'right' });

            yPosition += 8;

            // ── 8. SEPARATOR LINE ───────────────────────────────────────────────
            doc.setLineWidth(0.3);
            doc.setDrawColor(180, 180, 180);
            doc.line(20, yPosition, 190, yPosition);
            yPosition += 7;

            // ── 9. NOTES ────────────────────────────────────────────────────────
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(0, 0, 0);
            doc.text('Notes', 20, yPosition);
            yPosition += 5;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.text('It was great doing business with you.', 20, yPosition);
            yPosition += 9;

            // ── 10. TERMS & CONDITIONS ──────────────────────────────────────────
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.text('Terms & Conditions', 20, yPosition);
            yPosition += 5;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);

            const terms = [
                '1. Premium Used Auto Parts does not cover any labor costs for all items sold.',
                '2. If a part is damaged, the company will attempt to replace it. If no replacement is available, a full refund will be offered.',
                '3. If a customer cancels an order and doesn\'t want replacement part, Premium Used Auto Parts reserves the right to impose a 25% restocking fee and defer 2 way shipping cost for refunds.',
                '4. If a customer orders the wrong part and wants to return it, Premium Used Auto Parts reserves the right to impose a 25% restocking fee and defer 2 way shipping costs for refunds.',
                '5. If a customer orders a part and later decides to cancel the order, Premium Used Auto Parts has the right to impose a 25% restocking fee and may defer shipping costs when processing refunds.',
                '6. Engines are guaranteed against rod knock, cracked block, and excessive smoking for a period of 90 days from the date of purchase. Engine accessories are not guaranteed. Consider replacing gaskets and seals for improved performance.',
                '7. Transmissions are guaranteed to work properly at the time of purchase. Gears are guaranteed to be in good condition at time of purchase.',
                '8. The seller bears no responsibility for improper installation or usage after the purchase. The buyer waives claims for consequential damages, including labor charges, loss of income, or car rental fees. The sole remedy for the buyer is a return of the purchase price or an exchange for a replacement part if returned promptly. No warranties of merchantability or fitness for a particular purpose are provided beyond the description on the invoice.',
                '9. If a customer refuses to accept the package or does not respond to calls / email from the shipping company or premium used auto parts, the customer has to bear 25% restocking fee and two way shipping cost. Premium used auto parts are nowhere responsible for it.'
            ];

            terms.forEach((term) => {
                const splitTerm = doc.splitTextToSize(term, 165);
                splitTerm.forEach((line) => {
                    if (yPosition > 278) {
                        doc.addPage();
                        yPosition = 15;
                    }
                    doc.text(line, 20, yPosition);
                    yPosition += 4.5;
                });
                yPosition += 2;
            });
            
            // Save PDF
            doc.save(`Invoice_${invoiceData.invoiceNumber}.pdf`);
            message.success('PDF downloaded successfully!');

        } catch (error) {
            console.error('Error generating PDF:', error);
            message.error('Failed to generate PDF');
        } finally {
            setLoading(false);
        }
    };
    
    const handleCopyInvoiceNumber = () => {
        navigator.clipboard.writeText(invoiceData.invoiceNumber);
        message.success('Invoice number copied!');
    };

    return (
        <Modal
            open={visible}
            onCancel={onCancel}
            width={1000}
            style={{ top: 20 }}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button 
                    key="download" 
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadPDF}
                    loading={loading}
                    style={{ marginRight: 8 }}
                >
                    Download PDF
                </Button>,
                <Button
                    key="send"
                    type="primary"
                    onClick={handleSendToMail}
                    loading={loading}
                    icon={<MailOutlined />} 
                >
                    Send to Mail
                </Button>,
            ]}
        >
            <div style={{
                padding: '20px',
                backgroundColor: '#fff',
                fontFamily: 'Arial, sans-serif',
                fontSize: '12px'
            }}>
                {/* Company Header */}
                <Row gutter={[24, 24]} style={{
                    marginBottom: '20px',
                    borderBottom: '1px solid #000',
                    paddingBottom: '10px',
                }}>
                    <Col span={12}>
                        <div>
                            <img src={Logo} alt="Logo" 
                                style={{height:"100px",width:"auto",objectFit:"contain"}}
                            />
                        </div>
                    </Col>

                    <Col span={12} >
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '18px',color:"#004183" }}>
                                {invoiceData.companyInfo.name}
                            </div>
                            <div style={{ lineHeight: '1.6' }}>
                                <div>{invoiceData.companyInfo.address}</div>
                                <div>{invoiceData.companyInfo.cityStateZip}</div>
                                    <div>{invoiceData.companyInfo.phone}</div>
                                        <div>{invoiceData.companyInfo.email}</div>
                            </div>
                        </div>
                    </Col>
                </Row>
                
                {/* Billing Sections */}
                <Row gutter={[24, 24]} style={{ marginBottom: '20px' }}>
                    <Col span={12}>
                        <div>
                            <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '14px', color:"#004183" }}>
                                Bill To:
                            </div>
                            <div style={{ lineHeight: '1.6' }}>
                                <div>{invoiceData.billingInfo.name}</div>
                                <div>{invoiceData.billingInfo.address}</div>
                                <div>{invoiceData.billingInfo.cityStateZip}</div>
                            </div>
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '29px', fontFamily: 'none' ,color:"#004183"}}>
                                INVOICE
                            </div>
                    
                               <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                                Invoice No: <span style={{ fontWeight: "400" }}>{invoiceData.invoiceNumber}</span>
                            </div>
                            <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                                Invoice Date: <span style={{ fontWeight: "400" }}>{currentPSTDate}</span>
                            </div>
                            <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                                Due Date: <span style={{ fontWeight: "400" }}>{currentPSTDate}</span>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Invoice Items Table */}
                <Table
                    dataSource={invoiceData.lineItems}
                    columns={columns}
                    pagination={false}
                    size="small"
                    bordered
                    style={{ marginBottom: '20px' }}
                    rowClassName={() => 'invoice-table-row'}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{
                                    backgroundColor: '#004183',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    border: '1px solid #000'
                                }} />
                            ),
                        },
                    }}
                />

                {/* Totals Section */}
                <div style={{
                    marginLeft: 'auto',
                    width: '300px',
                    marginTop: '20px'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '5px'
                    }}>
                        <span>Sub Total</span>
                        <span>${invoiceData.summary.subtotal}</span>
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        borderTop: '1px solid #ddd',
                        paddingTop: '5px'
                    }}>
                        <span>Total</span>
                        <span>${invoiceData.summary.total}</span>
                    </div>
                </div>

                {/* Notes Section */}
                <div style={{
                    marginTop: '30px',
                    paddingTop: '10px',
                    borderTop: '1px solid #000'
                }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Notes</div>
                    <div>It was great doing business with you.</div>
                </div>

                {/* Terms & Conditions Section */}
                <div style={{
                    marginTop: '20px',
                    fontSize: '12px',
                    lineHeight: '1.3'
                }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Terms & Conditions</div>
                    <div>
                        <p>1.Premium Used Auto Parts does not cover any labor costs for all items sold.</p>
                         <p>2. If a part is damaged, the company will attempt to replace it. If no replacement is available, a full refund will be offered.</p>
                         <p>3. If a customer cancels an order and doesn't want replacement part, Premium Used Auto Parts reserves the right to impose a 25% restocking fee and defer 2 way shipping cost for refunds.</p>
                         <p>4. If a customer orders the wrong part and wants to return it, Premium Used Auto Parts reserves the right to impose a 25% restocking fee and defer 2 way shipping costs for refunds.</p>
                         <p>5. If a customer orders a part and later decides to cancel the order, Premium Used Auto Parts has the right to impose a 25% restocking fee and may defer shipping costs when processing refunds.</p>
                         <p>6. Engines are guaranteed against rod knock, cracked block, and excessive smoking for a period of 90 days from the date of purchase. Engine accessories are not guaranteed. Consider replacing gaskets and seals for improved performance.</p>
                         <p>7. Transmissions are guaranteed to work properly at the time of purchase. Gears are guaranteed to be in good condition at time of purchase.</p>
                        <p> 8. The seller bears no responsibility for improper installation or usage after the purchase. The buyer waives claims for consequential damages, including labor charges, loss of income, or car rental fees. The sole remedy for the buyer is a return of the purchase price or an exchange for a replacement part if returned promptly. No warranties of merchantability or fitness for a particular purpose are provided beyond the description on the invoice.</p>
                        <p> 9. If a customer refuses to accept the package or does not respond to calls / email from the shipping company or premium used auto parts, the customer has to bear 25% restocking fee and two way shipping cost. Premium used auto parts are nowhere responsible for it.</p>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default InvoicePreviewModal;